import { useAxios } from './axiosService';
import {
    allowJobUnavailable,
    allowUnavailable,
    isJobUnavailableStatus,
    isUnavailableStatus,
    reportUnavailableError,
} from '../components/reportPage/reportAccess';

// `?lang=` only when the page has a language, so the API falls back to the order's own language otherwise
const langParams = lang => (lang ? { params: { lang } } : {});

export const useReportService = () => {
    const { axios } = useAxios();

    // rejects with reportUnavailableError() when the job is not the user's or does not exist. `lang` is the page's
    // language: an order past the translation cut-off comes back in it (SC-QA-119)
    const getReport = async (uuid, lang) => {
        const response = await axios.get(`/jobs/${uuid}`, { ...allowJobUnavailable, ...langParams(lang) });
        if (isJobUnavailableStatus(response.status)) throw reportUnavailableError();
        return response.data;
    };

    // SC-QA-77: the VIN of someone else's order, answered only when the user has an order of their own for it; null
    // otherwise (404 for that, or for a missing order; 400 for a malformed uuid)
    const getOwnVinForOrder = async uuid => {
        if (!uuid) return null;

        const { status, data } = await axios.get(`/jobs/${uuid}/own-vin`, allowJobUnavailable);
        return isJobUnavailableStatus(status) ? null : data?.vin || null;
    };

    const getReportBySharedLink = async (sharedLink, lang) => {
        const response = await axios.get(`/jobs/shared-report/${sharedLink}`, langParams(lang));
        return response.data;
    };

    const getInternalReport = async (vin, lang = 'en') => {
        if (!vin) return null;

        let response;
        try {
            response = await axios.get(`/vehicle/internal-report/${vin}?lang=${lang}`, allowUnavailable);
        } catch (error) {
            console.error('Failed to fetch internal report:', error?.message);
            return null;
        }
        // no internal order of this user for the VIN (403), or the car is not in the base (404)
        if (isUnavailableStatus(response.status)) throw reportUnavailableError();
        return response.data;
    };

    const getInternalReportBySharedLink = async (sharedLink, lang = 'en') => {
        if (!sharedLink) return null;

        try {
            const { data } = await axios.get(`/vehicle/shared-report/${sharedLink}?lang=${lang}`);
            return data;
        } catch (error) {
            console.error('Failed to fetch shared internal report:', error?.message);
            return null;
        }
    };

    // a Complete order's AI overview from our internal data, for while the dealer part is not read yet or was refused:
    // { text, translation }, with text null when there is none (the page then says the AI overview is not available)
    const getInternalOverview = async (jobUuid, lang) => {
        if (!jobUuid) return { text: null, translation: null };

        try {
            const { status, data } = await axios.get(`/report/internal-overview/${jobUuid}`, {
                ...allowUnavailable,
                ...langParams(lang),
            });
            if (isUnavailableStatus(status)) return { text: null, translation: null };
            return { text: data?.overview || null, translation: data?.translation || null };
        } catch (error) {
            console.error('Failed to fetch internal overview:', error?.message);
            return { text: null, translation: null };
        }
    };

    const checkReportAvailability = async vin => {
        if (!vin) return { hasInternalReport: false };

        try {
            const { data } = await axios.get(`/vehicle/check-availability/${vin}`);
            return data;
        } catch (error) {
            console.error('Failed to check report availability:', error?.message);
            return { hasInternalReport: false, fillPercentage: 0 };
        }
    };

    // The public preview the landing VIN page shows, or null when the car is not in the base.
    // A 404 is the normal "not in the base" answer, so it must not reach the interceptor's error-page redirect.
    const getVehiclePreview = async vin => {
        if (!vin) return null;

        try {
            const { status, data } = await axios.get(`/vehicle/vin/${encodeURIComponent(vin)}`, {
                validateStatus: code => (code >= 200 && code < 300) || code === 404,
            });
            return status === 404 ? null : data;
        } catch (error) {
            console.error('Failed to fetch vehicle preview:', error?.message);
            return null;
        }
    };

    return {
        getReport,
        getReportBySharedLink,
        getOwnVinForOrder,
        getInternalReport,
        getInternalReportBySharedLink,
        getInternalOverview,
        checkReportAvailability,
        getVehiclePreview,
    };
};
